import ajax from '@ep/tools/ajax.js';
import toast from '@ep/tools/toast.js';
import { transformModulePriceRow } from './modulePriceDisplay.mjs';

const target = {
  product: 'platform',
  category: 'accounts',
  domain: 'billing',
  module: 'modulePrices',
};

const text = (key, fallback) => globalThis.dic?.modulePrices?.messages?.[key] || fallback;

const save = async ({ record, changes, component }) => {
  const data = Object.entries(changes).map(([name, value]) => ({ name, value }));
  data.push({ name: 'id', value: record.id });
  const result = await ajax.post({
    ...target,
    method: 'save',
    body: { key: 'main', data },
    config: { loader: false },
  });
  if (result?.status !== 'success') throw new Error(result?.error || result?.code || 'SAVE_FAILED');
  toast({ type: 'success', message: text('saved', 'Preço atualizado.') });
  await component?.render?.();
};

const isReviewDay = () => new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Sao_Paulo', month: '2-digit', day: '2-digit',
}).format(new Date()) === '01/02';

const canApprove = (record) => isReviewDay() && record?.suggestion_status === 'ready'
  && !!record?.suggestion_id && record?.is_active !== false
  && Number.isFinite(Number(record?.suggested_price_usd))
  && record?.suggested_price_usd != null;

const approve = async ({ record, component }) => {
  const result = await ajax.post({
    ...target,
    method: 'approveSuggestion',
    body: {
      price_id: record.id,
      suggestion_id: record.suggestion_id,
      expected_price: record.price ?? null,
    },
    config: { loader: false },
  });
  if (result?.status !== 'success') throw new Error(result?.code || 'BILLING_PRICE_APPROVAL_FAILED');
  toast({ type: 'success', message: text('saved', 'Preço atualizado.') });
  await component?.render?.();
};

const actionRegistry = () => ({
  modulePriceAcceptSuggestion: {
    visible: ({ record }) => canApprove(record),
    enabled: ({ record }) => canApprove(record),
    run: ({ record, component }) => approve({ record, component }),
  },
  modulePriceActivate: {
    label: 'dic.modulePrices.actions.activate',
    icon: { type: 'solid', name: 'toggle-on' },
    visible: ({ record }) => record?.is_active === false,
    run: ({ record, component }) => save({ record, component, changes: { is_active: true } }),
  },
  modulePriceDeactivate: {
    label: 'dic.modulePrices.actions.deactivate',
    icon: { type: 'solid', name: 'toggle-off' },
    visible: ({ record }) => record?.is_active !== false,
    run: ({ record, component }) => save({ record, component, changes: { is_active: false } }),
  },
});

const pageControl = {
  async mountBefore({ parent, createElement }) {
    if (!parent) return null;
    const host = document.createElement('section');
    host.className = 'module-prices-display-currency';
    host.setAttribute('aria-label', text('displayCurrency', 'Moeda de visualização'));
    parent.prepend(host);
    let selectedCurrency = 'USD';
    const combo = await createElement({
      element: 'combo',
      id: 'module-prices-display-currency',
      name: 'module-prices-display-currency',
      parent: host,
      hasFieldBox: false,
      label: text('displayCurrency', 'Moeda de visualização'),
      placeholder: text('chooseCurrency', 'Selecione a moeda'),
      scope: 'product',
      product: 'platform',
      service: 'accounts',
      category: 'accounts',
      domain: 'billing',
      module: 'currencies',
      key: 'activeOptions',
      add: false,
      value: 'USD',
      options: [],
    });
    const help = document.createElement('p');
    help.className = 'module-prices-display-currency-help';
    help.textContent = text('displayCurrencyHelp', 'O preço-base e a edição são sempre em USD; a moeda selecionada altera somente a visualização.');
    host.append(help);
    return {
      host,
      combo,
      transformRow: (record) => transformModulePriceRow(record, selectedCurrency, globalThis.dic?.modulePrices?.messages),
      setCurrency(value) {
        selectedCurrency = String(value || 'USD').trim().toUpperCase();
      },
    };
  },
  async mountAfter({ component, pageControl: state }) {
    if (!state?.combo) return;
    state.combo.addEventListener('change', async (event) => {
      const selected = String(event?.detail?.nextValue || 'USD').trim().toUpperCase();
      state.setCurrency(selected);
      await component?.render?.();
    });
    await component?.render?.();
  },
};

export function installModulePriceActions() {
  globalThis.__EP_DATAGRID_ACTION_REGISTRY = {
    ...(globalThis.__EP_DATAGRID_ACTION_REGISTRY || {}),
    ...actionRegistry(),
  };
  globalThis.__EP_DATAGRID_PAGE_CONTROLS = {
    ...(globalThis.__EP_DATAGRID_PAGE_CONTROLS || {}),
    'platform.accounts.billing.modulePrices': pageControl,
  };
}
